import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { menuItems } from "./MenuConfig";

export default function Sidebar() {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);

  const sidebarWidth = 82;

  return (
    <Box
      sx={{
        width: sidebarWidth,
        height: "100vh",
        bgcolor: "#ffffff",
        borderRight: "1px solid #ececec",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        py: 2,
        boxShadow: "2px 0 20px rgba(0,0,0,0.05)",
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          height: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <img
          src="/images/allforcare-logo.jpeg"
          alt="Logo"
          style={{ height: 60, width: 60, borderRadius: 12 }}
        />
      </Box>

      {/* Menu */}
      <List
        disablePadding
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        {menuItems.map((item, i) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          const hasSub = item.submenu?.length > 0;

          return (
            <Box
              key={item.title}
              sx={{ width: "100%", position: "relative" }}
              onMouseEnter={() => setHoveredItem(i)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {/* Main item */}
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  minHeight: 72,
                  borderRadius: 3,
                  mx: 1,
                  mb: 1.2,
                  flexDirection: "column",
                  justifyContent: "center",
                  transition: "all 0.25s",
                  bgcolor: active ? "rgba(80,120,255,0.08)" : "transparent",
                  "&:hover": {
                    bgcolor: "rgba(80,120,255,0.12)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mb: 0.7,
                    color: active ? "#1A73E8" : "#444",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Icon style={{ fontSize: 28 }} />
                </ListItemIcon>

                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: active ? "#1A73E8" : "#555",
                    letterSpacing: 0.2,
                    textAlign: "center",
                  }}
                />
              </ListItemButton>

              {/* Full-height Submenu */}
              {hasSub && (
                <Box
                  sx={{
                    position: "fixed",
                    top: 0,
                    left: sidebarWidth,
                    height: "100vh",
                    width: 260,
                    display: hoveredItem === i ? "flex" : "none",
                    flexDirection: "column",
                    backdropFilter: "blur(14px)",
                    background: "rgba(255,255,255,0.75)",
                    borderRight: "1px solid #e7e7e7",
                    boxShadow: "0 6px 30px rgba(0,0,0,0.12)",
                    borderRadius: "0px 16px 16px 0px",
                    zIndex: 1500,
                    overflow: "hidden",
                  }}
                >
                  {/* Submenu Title */}
                  <Box
                    sx={{
                      p: 3,
                      background: "linear-gradient(135deg, #f7f9ff 0%, #ffffff 100%)",
                      borderBottom: "1px solid #e5e7ea",
                    }}
                  >
                    <Typography
                      fontSize={15}
                      fontWeight={700}
                      color="#1A1A1A"
                      sx={{ letterSpacing: 0.4 }}
                    >
                      {item.title} Management
                    </Typography>
                  </Box>

                  {/* Submenu Items */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      px: 1.5,
                      py: 2,
                    }}
                  >
                    {item.submenu.map((sub) => {
                      const subActive = location.pathname === sub.path;
                      return (
                        <ListItemButton
                          key={sub.title}
                          component={Link}
                          to={sub.path}
                          sx={{
                            mb: 0.8,
                            borderRadius: 2,
                            height: 48,
                            px: 2,
                            bgcolor: subActive ? "rgba(80,120,255,0.15)" : "transparent",
                            color: subActive ? "#1A73E8" : "#333",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "rgba(80,120,255,0.10)",
                              transform: "translateX(5px)",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={sub.title}
                            primaryTypographyProps={{
                              fontSize: 14,
                              fontWeight: subActive ? 700 : 500,
                              letterSpacing: 0.3,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ py: 1.5, textAlign: "center" }}>
        <Typography variant="caption" color="#666">
          v2.0 • © 2026
        </Typography>
      </Box>
    </Box>
  );
}